import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth, isStudent } from "@/lib/auth";
import { MED_ACCEPT } from "@/components/requests/RequestAttachments";

const CATEGORY_LABEL = {
  permesso_assenza: "Permesso di assenza",
  visita_medica: "Visita medica / Sanità",
  certificato: "Richiesta certificato",
  prenotazione_esame: "Prenotazione esame",
  colloquio: "Colloquio con docente",
  generica: "Richiesta generica",
};

const CERT_TYPES = [
  { value: "iscrizione", label: "Certificato di iscrizione" },
  { value: "frequenza", label: "Certificato di frequenza" },
  { value: "esami", label: "Certificato esami sostenuti" },
  { value: "altro", label: "Altro certificato" },
];

const EMPTY = {
  category: "permesso_assenza",
  subject: "",
  description: "",
  date_from: "",
  date_to: "",
  half_day: false,
  time_from: "",
  time_to: "",
  cert_type: "iscrizione",
  target_teacher_id: "",
  preferred_date: "",
  student_id: "",
};

function calcHours(f) {
  try {
    if (!f.date_from) return 0;
    const d1 = new Date(f.date_from);
    const d2 = f.date_to ? new Date(f.date_to) : d1;
    const start = d1 < d2 ? d1 : d2;
    const end = d1 < d2 ? d2 : d1;
    const days = Math.floor((end - start) / 86400000) + 1;
    if (f.time_from && f.time_to) {
      const [h1, m1] = f.time_from.split(":").map(Number);
      const [h2, m2] = f.time_to.split(":").map(Number);
      const mins = (h2 * 60 + m2) - (h1 * 60 + m1);
      if (mins <= 0) return 0;
      return Math.round((mins / 60) * days * 100) / 100;
    }
    if (f.half_day) return days * 7;
    return days * 14;
  } catch (e) { return 0; }
}

export default function NewRequestDialog({ open, onOpenChange, onCreated, prefilledStudentId, initialCategory }) {
  const { user } = useAuth();
  const student = isStudent(user);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [hoursEdit, setHoursEdit] = useState("");
  const isLeave = form.category === "permesso_assenza" || form.category === "visita_medica";
  const isMedical = form.category === "visita_medica";
  const qc = useQueryClient();

  const { data: teachers = [] } = useQuery({
    queryKey: ["teachers-for-request"],
    queryFn: async () => (await api.get("/teachers")).data,
    enabled: open,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students-for-request"],
    queryFn: async () => (await api.get("/students", { params: { status: "attivo" } })).data,
    enabled: open && !student,
  });

  const { data: quota } = useQuery({
    enabled: open && student && (form.category === "permesso_assenza" || form.category === "visita_medica"),
    queryKey: ["my-leave-quota"],
    queryFn: async () => (await api.get("/leave-quotas/me")).data,
  });
  const hoursPreview = hoursEdit !== "" ? Number(String(hoursEdit).replace(",", ".")) || 0 : calcHours(form);

  useEffect(() => {
    if (open) { setForm({ ...EMPTY, category: initialCategory || EMPTY.category, student_id: prefilledStudentId || "" }); setFile(null); setHoursEdit(""); }
  }, [open, prefilledStudentId, initialCategory]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const mut = useMutation({
    mutationFn: async () => {
      const payload = { ...form, student_id: student ? undefined : form.student_id };
      if (isLeave && hoursPreview > 0) payload.hours = hoursPreview;
      if (isMedical) { payload.category = "permesso_assenza"; payload.medical = true; }
      const created = (await api.post("/requests", payload)).data;
      if (isMedical && file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/requests/${created.id}/attachments`, fd).catch(() => toast.error("Richiesta inviata, ma allegato non caricato: riprova dal dettaglio"));
      }
      return created;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["requests-stats-badge"] });
      toast.success("Richiesta inviata");
      onCreated?.(d);
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  function submit(e) {
    e.preventDefault();
    if (!form.subject.trim()) return toast.error("Inserisci un oggetto");
    if (!student && !form.student_id) return toast.error("Seleziona lo studente");
    mut.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuova richiesta</DialogTitle>
          <DialogDescription>Compila i dati; la segreteria didattica ti risponderà a breve.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Tipo di richiesta</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="req-category"><SelectValue/></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`req-cat-${k}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!student && (
            <div>
              <Label className="text-xs">Per conto dello studente</Label>
              <Select value={form.student_id || ""} onValueChange={(v) => set("student_id", v)}>
                <SelectTrigger data-testid="req-student"><SelectValue placeholder="Seleziona…"/></SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.matricola}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs">Oggetto</Label>
            <Input
              data-testid="req-subject"
              value={form.subject} onChange={(e) => set("subject", e.target.value)}
              placeholder="Breve titolo della richiesta"
              required
            />
          </div>

          {isLeave && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Dal</Label>
                <Input type="date" value={form.date_from} onChange={(e) => set("date_from", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Al</Label>
                <Input type="date" value={form.date_to} onChange={(e) => set("date_to", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Dalle (opz.)</Label>
                <Input type="time" value={form.time_from} onChange={(e) => set("time_from", e.target.value)} data-testid="req-time-from"/>
              </div>
              <div>
                <Label className="text-xs">Alle (opz.)</Label>
                <Input type="time" value={form.time_to} onChange={(e) => set("time_to", e.target.value)} data-testid="req-time-to"/>
              </div>
              {!(form.time_from && form.time_to) && (
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  <Checkbox id="half" checked={form.half_day} onCheckedChange={(v) => set("half_day", !!v)} data-testid="req-half-day"/>
                  <Label htmlFor="half" className="text-sm cursor-pointer">Mezza giornata</Label>
                </div>
              )}
              <div className="col-span-2 flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Ore {isMedical ? "della visita" : "da scalare"}</Label>
                <Input inputMode="decimal" value={hoursEdit !== "" ? hoursEdit : String(calcHours(form)).replace(".", ",")}
                  onChange={(e) => setHoursEdit(e.target.value.replace(/[^0-9.,]/g, ""))} className="h-8 w-24 text-right" data-testid="req-hours"/>
                <span className="text-[11px] text-stone-500">calcolate dal periodo, puoi correggerle</span>
              </div>
              {isMedical && (
                <div className="col-span-2 space-y-2">
                  <div className="text-[12px] rounded-md bg-sky-50 border border-sky-200 text-sky-900 px-3 py-2" data-testid="req-medical-note">
                    Le visite mediche <b>non scalano</b> dal monte ore permessi ({hoursPreview}h registrate a parte).
                  </div>
                  <div>
                    <Label className="text-xs">Ricetta / certificato (opz., anche dopo)</Label>
                    <Input type="file" accept={MED_ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="req-medical-file"/>
                  </div>
                </div>
              )}
              {!isMedical && quota && student && (
                <div className="col-span-2 flex items-center justify-between gap-3 rounded-md bg-stone-50 border border-stone-200 px-3 py-2 text-[12px]">
                  <div className="text-stone-600">
                    Monte ore permessi: <b className="text-[#1C352D]">{quota.total_hours}h</b> · Usate <b>{quota.used_hours}h</b> · Residue <b className={quota.available_hours < hoursPreview ? "text-red-700" : "text-emerald-700"}>{quota.available_hours}h</b>
                  </div>
                  <div className="text-stone-600">
                    Questa richiesta: <b className="text-[#1C352D]">{hoursPreview}h</b>
                  </div>
                </div>
              )}
              {!student && hoursPreview > 0 && (
                <div className="col-span-2 text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded px-2.5 py-1.5">
                  Ore richieste: <b className="text-[#1C352D]">{hoursPreview}h</b>
                </div>
              )}
            </div>
          )}

          {form.category === "certificato" && (
            <div>
              <Label className="text-xs">Tipo certificato</Label>
              <Select value={form.cert_type} onValueChange={(v) => set("cert_type", v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {CERT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {(form.category === "prenotazione_esame" || form.category === "colloquio") && (
            <>
              <div>
                <Label className="text-xs">Docente</Label>
                <Select value={form.target_teacher_id || "__none"} onValueChange={(v) => set("target_teacher_id", v === "__none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Non specificato</SelectItem>
                    {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Data preferita</Label>
                <Input type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} />
              </div>
            </>
          )}

          <div>
            <Label className="text-xs">Descrizione / motivazione</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder="Fornisci più dettagli possibili per aiutare la segreteria a decidere"/>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={mut.isPending} data-testid="req-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
              {mut.isPending ? "Invio…" : "Invia richiesta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
